import { Component } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GetIfcSettingsRequest, IfcToJsonSettings, SaveIfcSettingsRequest, SituWarpApiClient, IfcToJsonBuildingMaterialSettings, IIfcToJsonBuildingMaterialSettings } from 'situ-warpapi-client';
import { AuthService, BreadcrumbService, StateService, BannerService, BannerServiceStatus } from 'src/app/services';
import { BuildingMaterialTags } from 'src/app/shared/models/building-material-tags';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'app-ifc-building-materials-config',
  templateUrl: './ifc-building-materials-config.component.html',
  styleUrls: ['./ifc-building-materials-config.component.less'],
})
export class IfcBuildingMaterialsConfigComponent extends SituBaseComponent {
  buildingMaterialTagType = BuildingMaterialTags;
  buildingMaterialTagKeyValues = Object.keys(BuildingMaterialTags).filter(x => typeof BuildingMaterialTags[x as keyof BuildingMaterialTags] === "string")
    .map(key => ({ key: key, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: key, buildingMaterialTag: (BuildingMaterialTags as any)[key], shouldCreateSkirtingAndCornice: false }) }))
    .sort((a, b) => a.key.localeCompare(b.key));

  settings: IfcToJsonSettings;
  buildingMaterials: string[] = [];

  isNewBuildingMaterialFeed = true;
  buildingMaterialSettings: IfcToJsonBuildingMaterialSettings[] = [];
  newBuildingMaterialSettings: ExtendedIfcToJsonBuildingMaterialSettings[] = [];
  buildingMaterialSettingsPageIndex = 0;
  pagedBuildingMaterialSettings: IfcToJsonBuildingMaterialSettings[] = [];
  newBuildingMaterialSettingsPageIndex = 0;
  pagedNewBuildingMaterialSettings: ExtendedIfcToJsonBuildingMaterialSettings[] = [];

  artifactId: Guid | undefined;

  readonly pageSize = 200;

  private _loading = false;
  get loading() {
    return this._loading;
  }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(this._loading);
  }

  get settingsjson() {
    return JSON.stringify(this.settings);
  }

  get isBuildingMaterialSettingsDirty() {
    return JSON.stringify(this.settings.buildingMaterialSettings) != JSON.stringify(this.buildingMaterialSettings) ||
      this.newBuildingMaterialSettings.some(x => x.buildingMaterialTag !== undefined);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private warpApiClient: SituWarpApiClient,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private bannerService: BannerService) {
      super(authService, stateService);
    this.settings = this.getNewSettings();
  }

  protected onSelectedCompanyChange(): void {
    this.loadSettings(this.selectedCompany?.code!);
  }

  loadSettings(companyCode: string) {
    const request = { companyCode: companyCode, includeBuildingMaterials: true } as GetIfcSettingsRequest;
    this.loading = true;
    this.warpApiClient.getIfcSettings(request).then((response) => {
      if (!response.success) {
        this.snackBar.open("Failed to load.");
      }
      
      this.artifactId = Guid.parse(response.artifactId!);

      const newSettings = this.getNewSettings();
      let settings = response.settings || newSettings;
      if (!settings.roomSettings) {
        settings.roomSettings = newSettings.roomSettings;
      }
      if (!settings.buildingMaterialSettings) {
        settings.buildingMaterialSettings = newSettings.buildingMaterialSettings;
      }
      if (!settings.storeySettings) {
        settings.storeySettings = newSettings.storeySettings;
      }
      if (!settings.furnishingSettings) {
        settings.furnishingSettings = newSettings.furnishingSettings;
      }

      this.settings = settings;
      this.buildingMaterialSettings = JSON.parse(JSON.stringify(settings.buildingMaterialSettings))?.map((x: any) => new IfcToJsonBuildingMaterialSettings(x)) || [];

      this.buildingMaterialSettings.forEach( setting =>
      {
        if (setting.buildingMaterialTag != null) {
          return;
        }
      });

      this.pagedBuildingMaterialSettings = this.paginate(this.buildingMaterialSettings || [], this.pageSize, this.buildingMaterialSettingsPageIndex);

      this.buildingMaterials = response.buildingMaterials || [];

      const newBuildingMaterials = this.buildingMaterials.filter((buildingMaterial) => !!buildingMaterial && !this.buildingMaterialSettings.some(y => y.buildingMaterialName == buildingMaterial)) || [];

      this.newBuildingMaterialSettings = newBuildingMaterials.map((buildingMaterial) => new ExtendedIfcToJsonBuildingMaterialSettings({ buildingMaterialName: buildingMaterial, suggestedBuildingMaterialSettings: this.getSuggestedBuildingMaterial(buildingMaterial) }));
      this.pagedNewBuildingMaterialSettings = this.paginate(this.newBuildingMaterialSettings, this.pageSize, this.newBuildingMaterialSettingsPageIndex);

      this.loading = false;
    }).catch((e) => {
      this.loading = false;
      this.snackBar.open("Failed to load.");
    });
  }

  getNewSettings() {
    let settings = new IfcToJsonSettings();
    settings.builder = this.selectedCompany?.code;
    settings.buildingMaterialSettings = [];
    return settings;
  }

  onBuildingMaterialsPageChange(event: PageEvent) {
    this.buildingMaterialSettingsPageIndex = event.pageIndex;
    this.pagedBuildingMaterialSettings = this.paginate(this.buildingMaterialSettings, this.pageSize, this.buildingMaterialSettingsPageIndex);
  }
  onNewBuildingMaterialsPageChange(event: PageEvent) {
    this.newBuildingMaterialSettingsPageIndex = event.pageIndex;
    this.pagedNewBuildingMaterialSettings = this.paginate(this.newBuildingMaterialSettings, this.pageSize, this.newBuildingMaterialSettingsPageIndex);
  }

  toggleNewBuildingMaterialSettings() {
    this.isNewBuildingMaterialFeed = !this.isNewBuildingMaterialFeed;
  }

  removeBuildingMaterialSetting(buildingMaterialSetting: IfcToJsonBuildingMaterialSettings) {
    const index = this.buildingMaterialSettings!.indexOf(buildingMaterialSetting);
    let removedItems = this.buildingMaterialSettings!.splice(index, 1);

    let removedExtendedItems = removedItems.map((buildingMaterialSetting) =>
      new ExtendedIfcToJsonBuildingMaterialSettings({ buildingMaterialName: buildingMaterialSetting.buildingMaterialName, suggestedBuildingMaterialSettings: this.getSuggestedBuildingMaterial(buildingMaterialSetting.buildingMaterialName || "") }))
    this.newBuildingMaterialSettings.push(...removedExtendedItems);

    this.pagedBuildingMaterialSettings = this.paginate(this.buildingMaterialSettings, this.pageSize, this.buildingMaterialSettingsPageIndex);
    this.pagedNewBuildingMaterialSettings = this.paginate(this.newBuildingMaterialSettings, this.pageSize, this.newBuildingMaterialSettingsPageIndex);

    this.snackBar.open("Removed building material setting!");
  }

  showSuggestedBuildingMaterialSettings(buildingMaterialSetting: ExtendedIfcToJsonBuildingMaterialSettings) {
    return buildingMaterialSetting !== undefined && buildingMaterialSetting.suggestedBuildingMaterialSettings !== undefined &&
        (buildingMaterialSetting.buildingMaterialTag === undefined ||
          buildingMaterialSetting.shouldCreateSkirtingAndCornice !== buildingMaterialSetting.suggestedBuildingMaterialSettings.shouldCreateSkirtingAndCornice ||
          buildingMaterialSetting.buildingMaterialTag.replace(/[^a-z]+|(horizontal)|(vertical)/gi, '') !== buildingMaterialSetting.suggestedBuildingMaterialSettings.buildingMaterialTag?.replace(/[^A-Za-z]+/g, ''))
  }

  useSuggestedBuildingMaterialSettings(buildingMaterialSetting: ExtendedIfcToJsonBuildingMaterialSettings) {
    buildingMaterialSetting.buildingMaterialTag = buildingMaterialSetting.suggestedBuildingMaterialSettings?.buildingMaterialTag;
    buildingMaterialSetting.shouldCreateSkirtingAndCornice = buildingMaterialSetting.suggestedBuildingMaterialSettings?.shouldCreateSkirtingAndCornice;
  }

  getSuggestedBuildingMaterial(buildingMaterial: string): IIfcToJsonBuildingMaterialSettings | undefined {
    let match = this.findKeyMatch(buildingMaterial, this.buildingMaterialTagKeyValues, suggestedBuildingMaterialSettingsDictionary);

    if (!match) {
      return new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Generic Material", buildingMaterialTag: BuildingMaterialTags.GenericMaterial, shouldCreateSkirtingAndCornice: false });
    }

    return match;
  }

  findKeyMatch(text: string, keyValues?: { key: string, value: any }[], dictionary?: { key: RegExp, value: any }[]): any {
    let results: { key: string | RegExp, value: any }[];
    // See if the supplied dictionary RegEx has any hits
    results = dictionary?.filter(x => x.key.test(text)) || [];
    if (results.length) {
      return results[0].value;
    }

    const searchText = text.toLowerCase().replace(/[0-9]/g, '').replace(' ', '').trim();
    if (searchText.length <= 3) {
      // Don't try and do a generic match on such a short string
      return;
    }

    // See if any of the type enums directly match the searchText
    results = keyValues?.filter(x => x.key.toLowerCase().trim() == searchText) || [];
    if (results.length) {
      return results[0].value;
    }

    // See if any of the type enums contains the searchText
    results = keyValues?.filter(x => x.key.toLowerCase().trim().indexOf(searchText) >= 0) || [];
    if (results.length) {
      return results[0].value;
    }

    // See if the searchText contains any of the type enums
    results = keyValues?.filter(x => searchText.indexOf(x.key.toLowerCase().trim()) >= 0) || [];
    if (results.length) {
      return results[0].value;
    }
  }
  paginate(array: any[], pageSize: number, pageIndex: number) {
    return array.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize);
  }

  saveBuildingMaterialSettings() {
    if (!this.settings) {
      return;
    }

    const request = { latestArtifactId: this.artifactId!.toString(), settings: new IfcToJsonSettings({ builder: this.settings.builder }) } as SaveIfcSettingsRequest;
    request.settings!.roomSettings = this.settings.roomSettings;
    request.settings!.buildingMaterialSettings = this.buildingMaterialSettings.concat(this.newBuildingMaterialSettings).filter(x => x.buildingMaterialTag !== undefined) || [];
    request.settings!.storeySettings = this.settings.storeySettings;
    request.settings!.furnishingSettings = this.settings.furnishingSettings;

    this.loading = true;
    this.warpApiClient.saveIfcSettings(request).then((response) => {
      if (!response.success) {
        if (response.errorMessages && response.errorMessages.length > 0) {
          var message = `${response.errorMessages[0]}<br /><br />Please refresh the page and re-enter your changes again.`
          this.bannerService.AddMessage(message, BannerServiceStatus.error, this.artifactId, true);
        }
        this.snackBar.open("Failed to save building material settings.");
      }
      else {
        this.bannerService.RemoveMessage(this.artifactId!);
        this.snackBar.open("Saved building material settings!");

        this.artifactId = Guid.parse(response.artifactId!);

        this.settings.buildingMaterialSettings = JSON.parse(JSON.stringify(request.settings?.buildingMaterialSettings))?.map((x: any) => new IfcToJsonBuildingMaterialSettings(x)) || [];
        this.buildingMaterialSettings = request.settings?.buildingMaterialSettings || [];


        this.pagedBuildingMaterialSettings = this.paginate(this.buildingMaterialSettings || [], this.pageSize, this.buildingMaterialSettingsPageIndex);

        this.newBuildingMaterialSettings = this.newBuildingMaterialSettings.filter((buildingMaterial) => !this.buildingMaterialSettings.some(y => y.buildingMaterialName == buildingMaterial.buildingMaterialName && y.buildingMaterialTag != null)) || [];

        if (this.newBuildingMaterialSettings.length > 0 && this.newBuildingMaterialSettings.length < (this.newBuildingMaterialSettingsPageIndex * this.pageSize) + 1) {
          this.newBuildingMaterialSettingsPageIndex = Math.floor(this.newBuildingMaterialSettings.length / this.pageSize) - 1;
        }
        this.pagedNewBuildingMaterialSettings = this.paginate(this.newBuildingMaterialSettings, this.pageSize, this.newBuildingMaterialSettingsPageIndex);
      }
      this.loading = false;
    }).catch(() => {
      this.loading = false;
      this.snackBar.open("Failed to save building material settings.");
    });
  }
}

class ExtendedIfcToJsonBuildingMaterialSettings extends IfcToJsonBuildingMaterialSettings {
  suggestedBuildingMaterialSettings?: IIfcToJsonBuildingMaterialSettings;

  constructor(data?: IExtendedIfcToJsonBuildingMaterialSettings) {
    super(data);
  }
}
interface IExtendedIfcToJsonBuildingMaterialSettings extends IIfcToJsonBuildingMaterialSettings {
  suggestedBuildingMaterialSettings?: IIfcToJsonBuildingMaterialSettings;
}

// A collection of suggested building material settings
// Add the most specific matches at the top
// Simple equals and contains matches for suitably sized string are done later if the test fails to match and are mapped to a 'Generic Material'
const suggestedBuildingMaterialSettingsDictionary = [
  { key: /^(?=.*\bbrick\b)(?=.*\bsill\b|.*\bsoldier\b)(?!.*\brender|.*\bclad\b).*/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Brick Soldier", buildingMaterialTag: BuildingMaterialTags.BrickSoldier, shouldCreateSkirtingAndCornice: false }) },
  { key: /^(?=.*\bbrick\b)(?!.*\bsill\b|.*\brender|.*\bclad\b).*/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Brick Wall", buildingMaterialTag: BuildingMaterialTags.BrickWall, shouldCreateSkirtingAndCornice: false }) },

  { key: /^(?=.*\bwall\b)(?=.*\bplasterboard\b).*/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Plasterboard Wall", buildingMaterialTag: BuildingMaterialTags.PlasterboardWall, shouldCreateSkirtingAndCornice: true }) },

  { key: /render|stucco/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Brick Render", buildingMaterialTag: BuildingMaterialTags.BrickRender, shouldCreateSkirtingAndCornice: false }) },
  { key: /weatherboard|clad/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Cladding", buildingMaterialTag: BuildingMaterialTags.Cladding, shouldCreateSkirtingAndCornice: false }) },
  { key: /lamin/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Laminate", buildingMaterialTag: BuildingMaterialTags.Laminate, shouldCreateSkirtingAndCornice: false }) },

  { key: /stud|framing|cavity|void|\bair\b/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Not Themed", buildingMaterialTag: BuildingMaterialTags.NotThemed, shouldCreateSkirtingAndCornice: false }) },

  { key: /pen_/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Do Not Import", buildingMaterialTag: BuildingMaterialTags.DoNotImport, shouldCreateSkirtingAndCornice: false }) },

  { key: /roof/i, value: new IfcToJsonBuildingMaterialSettings({ buildingMaterialName: "Generic Material", buildingMaterialTag: BuildingMaterialTags.GenericMaterial, shouldCreateSkirtingAndCornice: false }) },
];
